import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { PipelineData, PipelineStep, PipelineSource, LogEntry, Artifact } from '../../services/mock-pipeline-data'
import { httpService } from '../../services/http-service'
import { utilService } from '../../services/util-service'
import { useCachedFetch } from '../../hooks/use-cached-fetch'
import { systemHasSchedule } from '../../services/schedule-utils'

export interface JobRunStatus {
  job_name: string
  job_run_id: string
  system_name: string
  state: 'STARTING' | 'RUNNING' | 'STOPPING' | 'STOPPED' | 'SUCCEEDED' | 'FAILED' | 'TIMEOUT' | 'ERROR' | 'UNKNOWN'
  started_on?: string
  completed_on?: string
  error_message?: string
  execution_time?: number
  aws_region?: string
}

const TERMINAL_STATES = ['SUCCEEDED', 'FAILED', 'STOPPED', 'TIMEOUT', 'ERROR']

interface ActivityLogEntry {
  time: string
  type: 'trigger' | 'status' | 'complete' | 'error' | 'lambda'
  message: string
  source?: string // groups entries by job/lambda name
}

// Reactive store: persists job runs + activity log across mount/unmount
const jobRunStore = new Map<string, JobRunStatus[]>()
const activityLogStore = new Map<string, ActivityLogEntry[]>()
const jobRunListeners = new Set<() => void>()

function notifyJobRunListeners() {
  jobRunListeners.forEach(fn => fn())
}

function setStoreRuns(acId: string, runs: JobRunStatus[]) {
  if (runs.length > 0) {
    jobRunStore.set(acId, runs)
  } else {
    jobRunStore.delete(acId)
  }
  notifyJobRunListeners()
}

function addLogEntry(acId: string, entry: ActivityLogEntry) {
  const log = activityLogStore.get(acId) || []
  log.push(entry)
  activityLogStore.set(acId, log)
  notifyJobRunListeners()
}

function nowTime(): string {
  return new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

/** Returns a summary for a given AC: 'running' | 'done' | null */
export function useJobRunIndicator(auditControlId: string | number): 'running' | 'done' | null {
  const acId = String(auditControlId)
  const [, forceUpdate] = useState(0)

  useEffect(() => {
    const listener = () => forceUpdate(n => n + 1)
    jobRunListeners.add(listener)
    return () => { jobRunListeners.delete(listener) }
  }, [])

  const runs = jobRunStore.get(acId)
  if (!runs || runs.length === 0) return null
  const hasNonTerminal = runs.some(r => !TERMINAL_STATES.includes(r.state))
  return hasNonTerminal ? 'running' : 'done'
}

/** Returns all AC IDs that have job runs, with their aggregate status */
export function useJobRunStates(): Map<string, 'running' | 'success' | 'error'> {
  const [, forceUpdate] = useState(0)

  useEffect(() => {
    const listener = () => forceUpdate(n => n + 1)
    jobRunListeners.add(listener)
    return () => { jobRunListeners.delete(listener) }
  }, [])

  const result = new Map<string, 'running' | 'success' | 'error'>()
  jobRunStore.forEach((runs, acId) => {
    if (runs.length === 0) return
    const hasNonTerminal = runs.some(r => !TERMINAL_STATES.includes(r.state))
    if (hasNonTerminal) {
      result.set(acId, 'running')
    } else {
      const hasError = runs.some(r => r.state === 'FAILED' || r.state === 'ERROR' || r.state === 'TIMEOUT')
      result.set(acId, hasError ? 'error' : 'success')
    }
  })
  return result
}

interface ExecutionPipelineProps {
  auditControlId: string
  auditControl?: any
  companyName?: string
}

const buildCloudWatchUrl = (jobRunId: string, region: string, logType: 'output' | 'error'): string => {
  const logGroup = encodeURIComponent(encodeURIComponent(`/aws-glue/python-jobs/${logType}`))
  return `https://${region}.console.aws.amazon.com/cloudwatch/home?region=${region}#logsV2:log-groups/log-group/${logGroup}/log-events/${jobRunId}`
}

const STEP_STAGES = ['consolidated', 'rule-manager', 'handler'] as const

const STAGE_LABELS: Record<string, string> = {
  'consolidated': 'Consolidation',
  'rule-manager': 'Rule Manager',
  'handler': 'Handler',
}

const hasError = (rec: any): boolean =>
  rec.metadata_json != null && typeof rec.metadata_json === 'object' && 'error' in rec.metadata_json

const formatTimestamp = (ts: string): string => utilService.formatDateTime(ts)

const getLatestTimestamp = (records: any[]): string | undefined => {
  if (records.length === 0) return undefined
  const latest = records.reduce((a: any, b: any) =>
    (a.max_created_at || a.created_at) > (b.max_created_at || b.created_at) ? a : b
  )
  return latest.max_created_at || latest.created_at
}

const findRawRecordsForSystem = (rawRecords: any[], system: any): any[] => {
  if (!system || rawRecords.length === 0) return []

  // Build candidate strings to match against output_key_name
  const candidates: string[] = []
  if (system.abbreviation) candidates.push(system.abbreviation.toLowerCase().replace(/[\s-]+/g, '_'))
  if (system.name) {
    candidates.push(system.name.toLowerCase().replace(/[\s-]+/g, '_'))
    // First word for multi-word names (e.g. "azure" from "Azure Active Directory")
    const firstWord = system.name.split(/\s+/)[0].toLowerCase()
    if (firstWord.length > 2) candidates.push(firstWord)
  }

  return rawRecords.filter(r => {
    const key = (r.output_key_name || '').toLowerCase()
    return candidates.some(c => key.includes(c) || c.includes(key))
  })
}

const getSourceStatus = (matchedRecords: any[]): 'completed' | 'exception' | 'pending' => {
  if (matchedRecords.length === 0) return 'pending'
  if (matchedRecords.some(hasError)) return 'exception'
  return 'completed'
}

const buildSources = (auditControl: any, rawRecords: any[]): PipelineSource[] => {
  const sources: PipelineSource[] = []

  const getLatestFileName = (records: any[]): string | undefined => {
    if (records.length === 0) return undefined
    const latest = records.reduce((a: any, b: any) =>
      (a.max_created_at || a.created_at) > (b.max_created_at || b.created_at) ? a : b
    )
    return latest.file_name
  }

  const mainSys = auditControl?.control?.mainSystem?.system
  if (mainSys?.name) {
    const matched = findRawRecordsForSystem(rawRecords, mainSys)
    sources.push({ label: mainSys.name, type: 'input', icon: 'filter', systemName: 'In Scope', abbreviation: mainSys.abbreviation, timestamp: getLatestTimestamp(matched), status: getSourceStatus(matched), fileName: getLatestFileName(matched) })
  }
  const hrSys = auditControl?.control?.hrSystem?.system
  if (hrSys?.name) {
    const matched = findRawRecordsForSystem(rawRecords, hrSys)
    sources.push({ label: hrSys.name, type: 'source', icon: 'people', systemName: 'HR', abbreviation: hrSys.abbreviation, timestamp: getLatestTimestamp(matched), status: getSourceStatus(matched), fileName: getLatestFileName(matched) })
  }
  const ticketSys = auditControl?.control?.ticketSystem?.system
  if (ticketSys?.name) {
    const matched = findRawRecordsForSystem(rawRecords, ticketSys)
    sources.push({ label: ticketSys.name, type: 'source', icon: 'ticket', systemName: 'Ticket', abbreviation: ticketSys.abbreviation, timestamp: getLatestTimestamp(matched), status: getSourceStatus(matched), fileName: getLatestFileName(matched) })
  }
  if (sources.length === 0) {
    sources.push({ label: 'No systems', type: 'input', icon: 'filter', status: 'pending' })
  }
  return sources
}

const getTs = (rec: any): string => rec.max_created_at || rec.created_at || ''

interface Iteration {
  start: string
  label: string
  forcedStages: Set<string>
  records: Record<string, any[]>
}

/**
 * Extract the timestamp suffix from a file_name.
 * e.g. "raw/vuvuzela_db_7062_1771242173.json" → "1771242173"
 *      "consolidated/vuvuzela_db__jira_7062_1771242173.json" → "1771242173"
 *      "flow-outputs/vuvuzela_db__jira_7062_1771242173_results.json" → "1771242173"
 */
const extractFileSuffix = (fileName: string): string | null => {
  if (!fileName) return null
  const base = fileName.replace(/^.*\//, '').replace(/\.json$/, '')
  const clean = base.replace(/_results$/, '')
  const match = clean.match(/_(\d{8,})$/)
  return match ? match[1] : null
}

/**
 * Extract system names from a consolidated/flow-outputs filename.
 * e.g. "consolidated/vuvuzela_db__jira_7062_1771242173.json" → ["vuvuzela_db", "jira"]
 */
const extractSystemNames = (fileName: string): string[] => {
  if (!fileName) return []
  const base = fileName.replace(/^.*\//, '').replace(/\.json$/, '').replace(/_results$/, '')
  // Remove the trailing _acId_timestamp (e.g. _7062_1771242173)
  const withoutSuffix = base.replace(/_\d+_\d{8,}$/, '')
  return withoutSuffix.split('__')
}

/**
 * Group execution records into iterations.
 *
 * Each iteration represents one "run" through (part of) the pipeline.
 * A normal run: raws → consolidation → rule-manager → handler.
 * Forced runs reuse an upstream stage's output:
 *   - Forced consolidation: reuses raw files from an older consolidation
 *   - Forced rule-manager: reruns RM on the same consolidated file
 *   - Forced handler: reruns handler on the same RM output
 *
 * The logic:
 * 1. For each consolidated suffix, sort RM records by time (ASC).
 *    Each RM record anchors a separate iteration.
 * 2. Handler records are assigned to the RM run they follow (by timestamp).
 *    Extra handlers after the last handler in a run = forced handler iterations.
 * 3. Consolidated records with no RM = incomplete iteration (stopped at consolidation).
 * 4. Orphan raw records = standalone incomplete iterations.
 *
 * Sorted newest first.
 */
const buildIterations = (executions: any[]): Iteration[] => {
  const byStage: Record<string, any[]> = {}
  for (const rec of executions) {
    if (!byStage[rec.stage]) byStage[rec.stage] = []
    byStage[rec.stage].push(rec)
  }

  const allRaw = (byStage['raw'] || []).slice().sort((a: any, b: any) => getTs(b).localeCompare(getTs(a)))
  const consolidated = byStage['consolidated'] || []
  const ruleManager = byStage['rule-manager'] || []
  const handler = byStage['handler'] || []

  // Index downstream by suffix, sorted by timestamp ASC (oldest first)
  const rmBySuffix: Record<string, any[]> = {}
  for (const rec of ruleManager) {
    const s = extractFileSuffix(rec.file_name)
    if (s) { if (!rmBySuffix[s]) rmBySuffix[s] = []; rmBySuffix[s].push(rec) }
  }
  for (const s of Object.keys(rmBySuffix)) {
    rmBySuffix[s].sort((a: any, b: any) => getTs(a).localeCompare(getTs(b)))
  }

  const hBySuffix: Record<string, any[]> = {}
  for (const rec of handler) {
    const s = extractFileSuffix(rec.file_name)
    if (s) { if (!hBySuffix[s]) hBySuffix[s] = []; hBySuffix[s].push(rec) }
  }
  for (const s of Object.keys(hBySuffix)) {
    hBySuffix[s].sort((a: any, b: any) => getTs(a).localeCompare(getTs(b)))
  }

  const sortedConsolidated = consolidated.slice().sort((a: any, b: any) => getTs(b).localeCompare(getTs(a)))
  const iterations: Iteration[] = []
  const rawsUsedByConsolidation = new Set<number>()

  for (const consRec of sortedConsolidated) {
    const suffix = extractFileSuffix(consRec.file_name)
    const systems = extractSystemNames(consRec.file_name)
    const consTs = getTs(consRec)

    // Find the latest raw per system created at or before the consolidation
    const rawRecs: any[] = []
    for (const sysName of systems) {
      const match = allRaw.find(r =>
        (r.output_key_name || '').toLowerCase() === sysName.toLowerCase() &&
        getTs(r) <= consTs
      )
      if (match) {
        rawRecs.push(match)
        rawsUsedByConsolidation.add(match.id)
      }
    }

    const suffixRMs = suffix ? (rmBySuffix[suffix] || []) : []
    const suffixHs = suffix ? (hBySuffix[suffix] || []) : []

    if (suffixRMs.length === 0) {
      // No RM records — iteration stopped at consolidation
      iterations.push({
        start: getTs(consRec),
        label: formatTimestamp(getTs(consRec)),
        forcedStages: new Set<string>(),
        records: { raw: rawRecs, consolidated: [consRec], 'rule-manager': [], handler: [] },
      })
    } else {
      // Each RM record = one iteration (first is normal, rest are forced RM)
      const usedHandlerIds = new Set<number>()

      for (let rmIdx = 0; rmIdx < suffixRMs.length; rmIdx++) {
        const rmRec = suffixRMs[rmIdx]
        const rmTs = getTs(rmRec)
        const nextRmTs = rmIdx < suffixRMs.length - 1 ? getTs(suffixRMs[rmIdx + 1]) : null

        // Find handlers created after this RM but before the next RM
        const matchedHandlers = suffixHs.filter(h => {
          if (usedHandlerIds.has(h.id)) return false
          const hTs = getTs(h)
          return hTs >= rmTs && (nextRmTs === null || hTs < nextRmTs)
        })

        if (matchedHandlers.length <= 1) {
          // Normal case: 0 or 1 handler for this RM run
          const hRec = matchedHandlers[0] || null
          if (hRec) usedHandlerIds.add(hRec.id)

          const latestRec = hRec || rmRec
          const forced = new Set<string>()
          if (rmIdx > 0) forced.add('rule-manager')

          iterations.push({
            start: getTs(latestRec),
            label: formatTimestamp(getTs(latestRec)),
            forcedStages: forced,
            records: { raw: rawRecs, consolidated: [consRec], 'rule-manager': [rmRec], handler: hRec ? [hRec] : [] },
          })
        } else {
          // Multiple handlers for one RM run — first is normal, rest are forced handler
          for (let hIdx = 0; hIdx < matchedHandlers.length; hIdx++) {
            const hRec = matchedHandlers[hIdx]
            usedHandlerIds.add(hRec.id)

            const forced = new Set<string>()
            if (rmIdx > 0) forced.add('rule-manager')
            if (hIdx > 0) forced.add('handler')

            iterations.push({
              start: getTs(hRec),
              label: formatTimestamp(getTs(hRec)),
              forcedStages: forced,
              records: { raw: rawRecs, consolidated: [consRec], 'rule-manager': [rmRec], handler: [hRec] },
            })
          }
        }
      }
    }
  }

  // Detect forced consolidation from metadata_json.is_force_consolidation flag
  for (const iter of iterations) {
    const consRec = iter.records.consolidated[0]
    if (consRec?.metadata_json?.is_force_consolidation) {
      iter.forcedStages.add('consolidated')
    }
  }

  // Raw records not referenced by any consolidation — group into iterations by 24h window
  const orphanRaws = allRaw.filter(r => !rawsUsedByConsolidation.has(r.id))
  const orphanGroups: any[][] = []
  for (const rawRec of orphanRaws) {
    const ts = new Date(getTs(rawRec)).getTime()
    // Try to add to an existing group within 24 hours of the group's first record
    const group = orphanGroups.find(g =>
      Math.abs(new Date(getTs(g[0])).getTime() - ts) < 24 * 60 * 60 * 1000
    )
    if (group) {
      group.push(rawRec)
    } else {
      orphanGroups.push([rawRec])
    }
  }
  for (const group of orphanGroups) {
    const latest = group.reduce((a: any, b: any) => getTs(a) > getTs(b) ? a : b)
    iterations.push({
      start: getTs(latest),
      label: formatTimestamp(getTs(latest)),
      forcedStages: new Set<string>(),
      records: {
        raw: group,
        consolidated: [],
        'rule-manager': [],
        handler: [],
      },
    })
  }

  iterations.sort((a, b) => b.start.localeCompare(a.start))
  return iterations
}

const buildPipelineDataForIteration = (iteration: Iteration | null, auditControl: any, hasExecutions: boolean): PipelineData => {
  const iterationByStage = iteration?.records || {}

  const rawRecords = iterationByStage['raw'] || []
  const sources = buildSources(auditControl, rawRecords)

  const steps: PipelineStep[] = STEP_STAGES.map(stage => {
    const records = iterationByStage[stage] || []
    const errors = records.filter(hasError)
    let status: PipelineStep['status'] = 'pending'
    let statusText = 'No records'
    if (records.length > 0 && errors.length > 0) {
      status = 'exception'
      statusText = `${errors.length} error${errors.length !== 1 ? 's' : ''}`
    } else if (records.length > 0) {
      status = stage === 'handler' ? 'success' : 'completed'
      statusText = `${records.length} file${records.length !== 1 ? 's' : ''}`
    }
    const latest = records.length > 0
      ? records.reduce((a: any, b: any) => getTs(a) > getTs(b) ? a : b)
      : null
    return {
      id: stage,
      label: STAGE_LABELS[stage],
      status,
      statusText,
      timestamp: latest ? getTs(latest) : undefined,
      fileName: latest?.file_name || (records.length > 0 ? records[0].file_name : undefined),
    }
  })

  const logs: LogEntry[] = []
  for (const stage of ['raw', ...STEP_STAGES]) {
    const records = iterationByStage[stage] || []
    for (const rec of records) {
      if (hasError(rec)) {
        logs.push({ timestamp: '', level: 'ERROR', message: `[${STAGE_LABELS[stage] || 'Raw'}] ${rec.metadata_json.error}` })
      }
    }
  }
  if (logs.length === 0 && hasExecutions) {
    logs.push({ timestamp: '', level: 'INFO', message: 'No errors detected in this iteration.' })
  }
  if (!hasExecutions) {
    logs.push({ timestamp: '', level: 'INFO', message: 'No execution records found.' })
  }

  const iterationRecords = Object.values(iterationByStage).flat()
  const getFileType = (fileName: string): Artifact['type'] => {
    if (fileName.endsWith('.pdf')) return 'pdf'
    if (fileName.endsWith('.csv')) return 'csv'
    return 'json'
  }

  const artifacts = iterationRecords
    .filter((rec: any) => rec.file_name)
    .map((rec: any) => ({
      fileName: rec.file_name,
      fileSize: rec.stage,
      type: getFileType(rec.file_name),
    }))

  return { steps, sources, logs, artifacts }
}

const IconCheck = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
)

const IconExclamation = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="9" x2="12" y2="13" />
    <circle cx="12" cy="17" r="0.5" fill="#fff" />
  </svg>
)

const IconClock = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
)

const IconFilter = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
)

const IconPeople = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
)

const IconTicket = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="M2 10h20" />
  </svg>
)

const IconFilePdf = () => (
  <span className="artifact-type-badge artifact-type-pdf">PDF</span>
)
const IconFileCsv = () => (
  <span className="artifact-type-badge artifact-type-csv">CSV</span>
)
const IconFileJson = () => (
  <span className="artifact-type-badge artifact-type-json">&lt; &gt;</span>
)

const IconDownload = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
)

const IconPlay = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <polygon points="5 3 19 12 5 21 5 3" />
  </svg>
)

const sourceIcons: Record<string, React.FC> = {
  filter: IconFilter,
  people: IconPeople,
  ticket: IconTicket,
  cloud: IconTicket,
  database: IconTicket,
}

const getStepIcon = (step: PipelineStep) => {
  switch (step.status) {
    case 'completed': return <IconCheck />
    case 'success': return <IconCheck />
    case 'exception': return <IconExclamation />
    default: return <IconClock />
  }
}


const IconChevronLeft = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
)

const IconChevronRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 6 15 12 9 18" />
  </svg>
)

const ExecutionPipeline: React.FC<ExecutionPipelineProps> = ({ auditControlId: rawAcId, auditControl, companyName }) => {
  const auditControlId = String(rawAcId)
  const { data: glueSchedules } = useCachedFetch('glue-triggers', async () => {
    const result = await httpService.get('/glue-schedules')
    return result.schedules as any[]
  })
  const entityName = auditControl?.audit?.entity?.name || ''

  const [iterationIndex, setIterationIndex] = useState(0)
  const [executions, setExecutions] = useState<any[]>([])
  const [isLoadingExec, setIsLoadingExec] = useState(true)
  const [fetchKey, setFetchKey] = useState(0)
  const [triggeringStage, setTriggeringStage] = useState<string | null>(null)
  const [triggerResult, setTriggerResult] = useState<{ success: boolean; message: string; details?: string[] } | null>(null)
  const [pendingTrigger, setPendingTrigger] = useState<{ preview: any; payload: any; label: string; hasGenericJobs?: boolean } | null>(null)
  const [genericAcIdOverride, setGenericAcIdOverride] = useState<string>('')
  const [activeJobRuns, _setActiveJobRuns] = useState<JobRunStatus[]>(() => jobRunStore.get(auditControlId) || [])
  const [activityLog, setActivityLog] = useState<ActivityLogEntry[]>(() => activityLogStore.get(auditControlId) || [])
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Wrap setter to keep the reactive store in sync
  const setActiveJobRuns = useCallback((update: JobRunStatus[] | ((prev: JobRunStatus[]) => JobRunStatus[])) => {
    _setActiveJobRuns(prev => {
      const next = typeof update === 'function' ? update(prev) : update
      setStoreRuns(auditControlId, next)
      return next
    })
  }, [auditControlId])

  const appendLog = useCallback((type: ActivityLogEntry['type'], message: string, source?: string) => {
    const entry: ActivityLogEntry = { time: nowTime(), type, message, source }
    addLogEntry(auditControlId, entry)
    setActivityLog(activityLogStore.get(auditControlId) || [])
  }, [auditControlId])

  // No auto-scroll — the activity log is at the bottom and scrollIntoView would jump the page

  // Poll active (non-terminal) job runs every 5 seconds.
  // Reads from jobRunStore directly to avoid stale closures in setInterval.
  const pollJobRuns = useCallback(async () => {
    const currentRuns = jobRunStore.get(auditControlId) || []
    const nonTerminal = currentRuns.filter(r => !TERMINAL_STATES.includes(r.state))
    if (nonTerminal.length === 0) return

    const runsParam = nonTerminal.map(r => `${r.job_name}:${r.job_run_id}`).join(',')
    try {
      const data = await httpService.get('job-run-status', { runs: runsParam })
      setActiveJobRuns(prev => {
        const updated = prev.map(run => {
          const match = data.statuses?.find((s: any) => s.job_run_id === run.job_run_id)
          if (match && match.state !== run.state) {
            const isTerminal = TERMINAL_STATES.includes(match.state)
            const type: ActivityLogEntry['type'] = match.state === 'FAILED' || match.state === 'ERROR' || match.state === 'TIMEOUT' ? 'error' : isTerminal ? 'complete' : 'status'
            const suffix = match.execution_time ? ` (${Math.round(match.execution_time)}s)` : ''
            const errSuffix = match.error_message && isTerminal ? ` — ${match.error_message}` : ''
            const entry: ActivityLogEntry = { time: nowTime(), type, message: `${match.state}${suffix}${errSuffix}`, source: run.system_name }
            addLogEntry(auditControlId, entry)
            setActivityLog(activityLogStore.get(auditControlId) || [])
            return { ...run, ...match }
          }
          return match ? { ...run, ...match } : run
        })
        return updated
      })
    } catch (e) {
      console.error('Failed to poll job status', e)
    }
  }, [auditControlId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const nonTerminal = activeJobRuns.filter(r => !TERMINAL_STATES.includes(r.state))

    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }

    if (nonTerminal.length === 0) {
      // All finished — refresh execution history if we had runs
      if (activeJobRuns.length > 0 && activeJobRuns.every(r => TERMINAL_STATES.includes(r.state))) {
        refresh()
      }
      return
    }

    pollJobRuns()
    pollingRef.current = setInterval(pollJobRuns, 5000)

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
        pollingRef.current = null
      }
    }
  }, [activeJobRuns.map(r => `${r.job_run_id}:${r.state}`).join(',')]) // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current)
    }
  }, [])

  useEffect(() => {
    if (!auditControlId) return
    setIsLoadingExec(true)
    httpService.get('execution-history', { audit_control_id: auditControlId, ...(companyName ? { company_name: companyName } : {}) })
      .then((data: any[]) => { setExecutions(data); setIterationIndex(0) })
      .catch(() => setExecutions([]))
      .finally(() => setIsLoadingExec(false))
  }, [auditControlId, fetchKey])

  const refresh = () => setFetchKey(k => k + 1)

  const triggerStage = async (stage: string, inputFile?: string, systemName?: string | string[], overrideAcId?: number) => {
    const stageLabels: Record<string, string> = {
      'raw': 'ETL (Raw)',
      'consolidation': 'Consolidation',
      'rule-manager': 'Rule Manager',
      'handler': 'Handler',
    }
    const displayName = Array.isArray(systemName) ? systemName[0] : systemName
    const label = displayName ? `${displayName} ETL` : (stageLabels[stage] || stage)

    setTriggeringStage(displayName ? `raw-${displayName}` : stage)
    setTriggerResult(null)
    try {
      const payload: any = { stage, audit_control_id: Number(auditControlId), dry_run: true, ...(companyName ? { company_name: companyName } : {}) }
      if (inputFile) payload.input_file = inputFile
      if (systemName) payload.system_name = systemName
      if (overrideAcId) payload.override_audit_control_id = overrideAcId
      const preview = await httpService.post('trigger-stage', payload)

      if (preview.dry_run) {
        if (!preview.actions?.length) {
          setTriggerResult({ success: false, message: preview.message || `No actions found for ${label}` })
          setTriggeringStage(null)
          return
        }
        // Show confirmation modal with preview data
        const hasGenericJobs = (preview.actions || []).some((a: any) => a.is_generic)
        setPendingTrigger({ preview, payload, label, hasGenericJobs })
        setGenericAcIdOverride('')
      } else {
        // Backend doesn't support dry_run — already executed
        setTriggerResult({
          success: !preview.errors?.length,
          message: preview.message || `${label} triggered`,
          details: preview.function_name ? [`Function: ${preview.function_name}`] : undefined,
        })
        setTimeout(() => refresh(), 5000)
        setTriggeringStage(null)
      }
    } catch (err: any) {
      setTriggerResult({ success: false, message: err?.message || `Failed to trigger ${label}` })
      setTriggeringStage(null)
    }
  }

  const confirmTrigger = async () => {
    if (!pendingTrigger) return
    const { payload, label } = pendingTrigger
    const acIdOverride = genericAcIdOverride.trim()
    setPendingTrigger(null)
    setGenericAcIdOverride('')
    const logMessage = acIdOverride ? `Triggered ETL with Audit Control ID ${acIdOverride}` : 'Triggered ETL'
    try {
      payload.dry_run = false
      if (acIdOverride) payload.override_audit_control_id = Number(acIdOverride)
      const res = await httpService.post('trigger-stage', payload)

      // Handle ETL triggers — triggered is now an array of {job_name, job_run_id, system_name}
      if (res.triggered?.length) {
        const newRuns: JobRunStatus[] = res.triggered
          .filter((t: any) => typeof t === 'object' && t.job_run_id)
          .map((t: any) => ({
            job_name: t.job_name,
            job_run_id: t.job_run_id,
            system_name: t.system_name || t.job_name,
            state: 'STARTING' as const,
            aws_region: res.aws_region,
          }))
        for (const run of newRuns) {
          appendLog('trigger', logMessage, run.system_name)
          appendLog('status', 'STARTING', run.system_name)
        }
        if (newRuns.length > 0) {
          setActiveJobRuns(prev => [...prev, ...newRuns])
        }
      }

      // Log errors and show feedback banner
      if (res.errors?.length) {
        for (const e of res.errors) {
          appendLog('error', e.error, e.job_name)
        }
        setTriggerResult({ success: false, message: res.message || `${label} had errors` })
      }

      // Lambda invocations (consolidation, rule-manager, handler)
      if (res.function_name) {
        const lambdaStatus = res.lambda_status // 'success' | 'error' | 'running' (timed out)
        appendLog('trigger', `Triggered ${label}`, label)

        let logType: ActivityLogEntry['type'] = 'complete'
        let logMessage = 'Completed successfully'
        let jobState: JobRunStatus['state'] = 'SUCCEEDED'

        if (lambdaStatus === 'error') {
          logType = 'error'
          const rawError = res.lambda_error || res.message || 'Failed'
          logMessage = rawError.startsWith('[ERROR]') ? rawError : `[ERROR] ${rawError}`
          jobState = 'FAILED'
        } else if (lambdaStatus === 'running') {
          logType = 'status'
          logMessage = 'Still running (waiting for result...)'
          jobState = 'RUNNING'
        }

        appendLog(logType, logMessage, label)
        setActiveJobRuns(prev => [...prev, {
          job_name: res.function_name,
          job_run_id: `lambda-${Date.now()}`,
          system_name: label,
          state: jobState,
        }])
      }
      if (!res.triggered?.length) setTimeout(() => refresh(), 5000)
    } catch (err: any) {
      appendLog('error', err?.message || 'Failed', label)
      setTriggerResult({ success: false, message: err?.message || `Failed to trigger ${label}` })
    } finally {
      setTriggeringStage(null)
    }
  }

  const cancelTrigger = () => {
    setPendingTrigger(null)
    setTriggeringStage(null)
  }

  const [downloadingFile, setDownloadingFile] = useState<string | null>(null)

  const downloadArtifact = async (fileKey: string) => {
    setDownloadingFile(fileKey)
    try {
      const res = await httpService.get('download-artifact', {
        audit_control_id: auditControlId,
        file_key: fileKey,
        ...(companyName ? { company_name: companyName } : {}),
      })
      if (res.url) {
        const a = document.createElement('a')
        a.href = res.url
        a.download = fileKey.split('/').pop() || fileKey
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      }
    } catch (err) {
      console.error('Failed to download artifact:', err)
      setTriggerResult({ success: false, message: `Failed to download ${fileKey.split('/').pop()}` })
    } finally {
      setDownloadingFile(null)
    }
  }

  const [wpStatus, setWpStatus] = useState<'idle' | 'generating' | 'success' | 'error'>('idle')
  const wpPollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const generateWorkingPaper = async () => {
    setWpStatus('generating')
    try {
      const res = await httpService.post('generate-working-paper', {
        audit_control_id: auditControlId,
        output_type: 'zip',
        ...(companyName ? { company_name: companyName } : {}),
      })
      const notificationId = res.notification_id
      const wpCompanyName = res.company_name
      if (!notificationId || !wpCompanyName) {
        setWpStatus('error')
        setTriggerResult({ success: false, message: 'Failed to start working paper generation' })
        return
      }

      // Poll notification status every 3 seconds
      wpPollRef.current = setInterval(async () => {
        try {
          const status = await httpService.get('working-paper-status', {
            notification_id: String(notificationId),
            company_name: wpCompanyName,
          })
          if (status.status === 'SUCCESS' || status.status === 'WARNING') {
            if (wpPollRef.current) clearInterval(wpPollRef.current)
            wpPollRef.current = null
            setWpStatus('success')
            if (status.url) {
              // The WP generator returns the presigned URL directly in response_data
              const url = typeof status.url === 'string' ? status.url : ''
              if (url) {
                const a = document.createElement('a')
                a.href = url
                a.download = `working_paper_${auditControlId}.zip`
                document.body.appendChild(a)
                a.click()
                document.body.removeChild(a)
              }
            }
            setTriggerResult({ success: true, message: status.status === 'WARNING' ? 'Working paper generated with warnings' : 'Working paper downloaded' })
            setTimeout(() => setWpStatus('idle'), 3000)
          } else if (status.status === 'ERROR') {
            if (wpPollRef.current) clearInterval(wpPollRef.current)
            wpPollRef.current = null
            setWpStatus('error')
            setTriggerResult({ success: false, message: 'Working paper generation failed' })
            setTimeout(() => setWpStatus('idle'), 5000)
          }
        } catch {
          // Keep polling on network errors
        }
      }, 3000)
    } catch (err) {
      console.error('Failed to generate working paper:', err)
      setWpStatus('error')
      setTriggerResult({ success: false, message: 'Failed to start working paper generation' })
      setTimeout(() => setWpStatus('idle'), 5000)
    }
  }

  // Clean up WP polling on unmount
  useEffect(() => {
    return () => {
      if (wpPollRef.current) clearInterval(wpPollRef.current)
    }
  }, [])

  // Auto-dismiss trigger feedback after 8s
  useEffect(() => {
    if (!triggerResult) return
    const timer = setTimeout(() => setTriggerResult(null), 8000)
    return () => clearTimeout(timer)
  }, [triggerResult])

  const iterations = useMemo(() => buildIterations(executions), [executions])
  const currentIteration = iterations[iterationIndex] || null
  const data: PipelineData = useMemo(
    () => buildPipelineDataForIteration(currentIteration, auditControl, executions.length > 0),
    [currentIteration, auditControl, executions.length]
  )

  const totalIterations = iterations.length
  const canGoNewer = iterationIndex > 0
  const canGoOlder = iterationIndex < totalIterations - 1

  // Find the index of the latest successful iteration (handler completed without errors)
  const lastSuccessIndex = useMemo(() => {
    return iterations.findIndex(iter => {
      const handlerRecs = iter.records['handler'] || []
      return handlerRecs.length > 0 && !handlerRecs.some(hasError)
    })
  }, [iterations])

  if (isLoadingExec) {
    return (
      <div className="execution-pipeline">
        <span className="pipeline-title">EXECUTION PIPELINE</span>
        <div className="pipeline-loading">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="spinning">
            <polyline points="23 4 23 10 17 10" />
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
          </svg>
          <span>Loading Execution Pipeline...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="execution-pipeline">
      <span className="pipeline-title">EXECUTION PIPELINE</span>
      <div className="pipeline-body">
        <div className="pipeline-left">
          <div className="pipeline-header">
            {totalIterations > 0 && (
              <div className="iteration-nav">
                <button className="iter-btn" disabled={!canGoOlder} onClick={() => setIterationIndex(i => i + 1)} aria-label="Older iteration">
                  <IconChevronLeft />
                </button>
                <span className="iter-label">
                  {currentIteration?.label || ''} (Iteration {iterationIndex + 1}/{totalIterations})
                </span>
                <button className="iter-btn" disabled={!canGoNewer} onClick={() => setIterationIndex(i => i - 1)} aria-label="Newer iteration">
                  <IconChevronRight />
                </button>
                {lastSuccessIndex >= 0 && (
                  <button
                    className={`iter-jump-btn ${lastSuccessIndex === iterationIndex ? 'active' : ''}`}
                    disabled={lastSuccessIndex === iterationIndex}
                    onClick={() => setIterationIndex(lastSuccessIndex)}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#2e7d32" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    Last Success
                  </button>
                )}
                <button className="refresh-btn" onClick={refresh} disabled={isLoadingExec} aria-label="Refresh pipeline">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={isLoadingExec ? 'spinning' : ''}>
                    <polyline points="23 4 23 10 17 10" />
                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                  </svg>
                </button>
              </div>
            )}
          </div>

          {triggerResult && (
            <div className={`trigger-feedback trigger-feedback-${triggerResult.success ? 'success' : 'error'}`}>
              <div className="trigger-feedback-body">
                <span className="trigger-feedback-message">{triggerResult.message}</span>
                {triggerResult.details && (
                  <div className="trigger-feedback-details">
                    {triggerResult.details.map((d, i) => <span key={i}>{d}</span>)}
                  </div>
                )}
              </div>
              <button className="trigger-feedback-close" onClick={() => setTriggerResult(null)}>&times;</button>
            </div>
          )}
          <div className="pipeline-viz">
          <div className="pipeline-sources">
            {data.sources.map((source, i) => {
              const SourceIcon = sourceIcons[source.icon] || IconFilter
              const sourceName = source.label.toLowerCase()
              const sourceAbbr = source.abbreviation?.toLowerCase()
              const isSourceRunning =
                triggeringStage === `raw-${source.label}` ||
                activeJobRuns.some(r =>
                  !TERMINAL_STATES.includes(r.state) && (
                    r.system_name === source.label ||
                    r.system_name.toLowerCase().includes(sourceName) ||
                    (sourceAbbr && r.system_name.toLowerCase().includes(sourceAbbr))
                  )
                )
              const hasSchedule = !glueSchedules || !entityName || systemHasSchedule(entityName, source.label, source.abbreviation, glueSchedules)
              return (
                <div key={i} className={`source-item source-${isSourceRunning ? 'running' : (source.status || 'pending')}${hasSchedule ? ' triggerable' : ''}`} title={source.fileName || ''}>
                  <div className="source-icon-wrapper">
                    {isSourceRunning
                      ? <span className="source-icon source-spinner" />
                      : <span className="source-icon"><SourceIcon /></span>
                    }
                    {hasSchedule && <button
                      className="source-trigger-overlay"
                      disabled={triggeringStage !== null}
                      onClick={(e) => { e.stopPropagation(); triggerStage('raw', undefined, [source.label, source.abbreviation].filter(Boolean) as string[]) }}
                      title={`Trigger ETL for ${source.label}`}
                    >
                      <IconPlay />
                    </button>}
                  </div>
                  <div className="source-info">
                    <span className="source-label">{source.label}</span>
                    <span className="source-type">{source.systemName || (source.type === 'input' ? 'In Scope' : 'Source')}</span>
                    {source.timestamp && <span className="source-timestamp">{formatTimestamp(source.timestamp)}</span>}
                  </div>
                </div>
              )
            })}
          </div>

          <div className="pipeline-connector-horizontal" />

          <div className="pipeline-steps">
            {data.steps.map((step, i) => {
              const handleStepTrigger = () => {
                if (step.id === 'consolidated') {
                  triggerStage('consolidation')
                } else if (step.id === 'rule-manager') {
                  const consFile = currentIteration?.records?.consolidated?.[0]?.file_name
                  if (!consFile) {
                    setTriggerResult({ success: false, message: 'No consolidated file available in this iteration' })
                    return
                  }
                  triggerStage('rule-manager', consFile)
                } else if (step.id === 'handler') {
                  const rmFile = currentIteration?.records?.['rule-manager']?.[0]?.file_name
                  if (!rmFile) {
                    setTriggerResult({ success: false, message: 'No Rule Manager output file available in this iteration' })
                    return
                  }
                  triggerStage('handler', rmFile)
                }
              }

              return (
                <React.Fragment key={step.id}>
                  <div className={`pipeline-step step-${step.status} triggerable`} title={step.fileName || ''}>
                    <div className="step-circle-wrapper">
                      <div className="step-circle">{getStepIcon(step)}</div>
                      <button
                        className="step-trigger-overlay"
                        disabled={triggeringStage !== null}
                        onClick={handleStepTrigger}
                        title={`Trigger ${step.label}`}
                      >
                        <IconPlay />
                      </button>
                    </div>
                    <div className="step-label">{step.label}</div>
                    {step.timestamp && <div className="step-timestamp">{formatTimestamp(step.timestamp)}</div>}
                    {currentIteration?.forcedStages.has(step.id) && (
                      <div className="step-forced-label">Forced {step.label}</div>
                    )}
                  </div>
                  {i < data.steps.length - 1 && <div className="step-line" />}
                </React.Fragment>
              )
            })}
          </div>
          </div>

          {data.logs.some(l => l.level === 'ERROR') && (
            <div className="pipeline-errors">
              <div className="pipeline-errors-icon"><IconExclamation /></div>
              <div className="pipeline-errors-list">
                {data.logs.filter(l => l.level === 'ERROR').map((log, i) => (
                  <div key={i} className="pipeline-error-line">
                    {log.timestamp && <span className="error-timestamp">{log.timestamp}</span>}
                    <span className="error-message">{log.message}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activityLog.length > 0 && (() => {
            const grouped: Record<string, ActivityLogEntry[]> = {}
            const sourceOrder: string[] = []
            for (const entry of activityLog) {
              const key = entry.source || '_general'
              if (!grouped[key]) {
                grouped[key] = []
                sourceOrder.push(key)
              }
              grouped[key].push(entry)
            }
            return (
              <div className="activity-log">
                <div className="activity-log-header">
                  <span className="activity-log-title">TRIGGERED RUNS</span>
                </div>
                <div className="activity-log-runs-wrapper">
                <div className="activity-log-runs">
                  {sourceOrder.filter(s => s !== '_general').map(source => {
                    const entries = grouped[source]
                    const lastEntry = entries[entries.length - 1]
                    const isComplete = lastEntry?.type === 'complete'
                    const isError = lastEntry?.type === 'error'
                    const isRunning = !isComplete && !isError && lastEntry?.type !== 'trigger'
                    const laneStatus = isError ? 'error' : isComplete ? 'complete' : isRunning ? 'running' : 'pending'
                    const run = activeJobRuns.find(r => r.system_name === source)
                    return (
                      <div key={source} className={`run-card run-${laneStatus}`}>
                        <div className="run-card-header">
                          <span className={`run-status-dot run-dot-${laneStatus}`} />
                          <span className="run-name">{source}</span>
                          {run?.aws_region && run.job_run_id && (
                            <span className="run-links">
                              <a href={buildCloudWatchUrl(run.job_run_id, run.aws_region, 'output')} target="_blank" rel="noopener noreferrer" className="run-link" title="Output log">Log</a>
                              <a href={buildCloudWatchUrl(run.job_run_id, run.aws_region, 'error')} target="_blank" rel="noopener noreferrer" className="run-link run-link-error" title="Error log">Err</a>
                            </span>
                          )}
                        </div>
                        <div className="run-card-entries">
                          {entries.map((entry, i) => (
                            <div key={i} className={`activity-log-entry activity-log-${entry.type}`}>
                              <span className="activity-log-time">{entry.time}</span>
                              <span className="activity-log-message">{entry.message}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  })}
                </div>
                </div>
              </div>
            )
          })()}
        </div>

        <div className="pipeline-right">
          <div className="downloads-header">DOWNLOADS</div>
          <button
            className={`dl-wp dl-${wpStatus}`}
            onClick={generateWorkingPaper}
            disabled={wpStatus === 'generating'}
          >
            <div className="dl-wp-icon">
              {wpStatus === 'generating'
                ? <span className="dl-spinner" />
                : wpStatus === 'success'
                  ? <IconCheck />
                  : <IconDownload />
              }
            </div>
            <div className="dl-wp-body">
              <span className="dl-wp-title">
                {wpStatus === 'generating' ? 'Generating...' : wpStatus === 'success' ? 'Downloaded' : 'Working Paper + IPEs'}
              </span>
              <span className="dl-wp-sub">ZIP — Excel report with all IPE files</span>
            </div>
          </button>

          {data.artifacts.length > 0 && (
            <div className="dl-artifacts-label">Pipeline Files</div>
          )}
          <div className="dl-artifacts">
            {data.artifacts.length === 0 ? (
              <span className="dl-artifacts-empty">No pipeline artifacts in this iteration</span>
            ) : (() => {
              const grouped: Record<string, Artifact[]> = {}
              for (const a of data.artifacts) {
                const stage = a.fileSize || 'other'
                if (!grouped[stage]) grouped[stage] = []
                grouped[stage].push(a)
              }
              return Object.entries(grouped).map(([stage, artifacts]) => (
                <React.Fragment key={stage}>
                  {artifacts.map((artifact, i) => (
                    <button
                      key={`${stage}-${i}`}
                      className={`dl-file${downloadingFile === artifact.fileName ? ' dl-downloading' : ''}`}
                      onClick={() => downloadArtifact(artifact.fileName)}
                      disabled={downloadingFile !== null}
                      title={artifact.fileName.split('/').pop()}
                    >
                      {downloadingFile === artifact.fileName
                        ? <span className="dl-spinner dl-spinner-sm" />
                        : <span className="dl-file-badge">
                            {artifact.type === 'pdf' ? <IconFilePdf /> : artifact.type === 'csv' ? <IconFileCsv /> : <IconFileJson />}
                          </span>
                      }
                      <span className="dl-file-stage">{stage}</span>
                      <span className="dl-file-name">{artifact.fileName.split('/').pop()}</span>
                    </button>
                  ))}
                </React.Fragment>
              ))
            })()}
          </div>
        </div>
      </div>

      {pendingTrigger && (
        <div className="trigger-confirm-overlay" onClick={cancelTrigger}>
          <div className="trigger-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="trigger-confirm-header">
              Confirm Trigger: {pendingTrigger.label}
            </div>
            <div className="trigger-confirm-body">
              {(pendingTrigger.preview.actions || []).map((action: any, i: number) => (
                <div key={i} className="trigger-confirm-action">
                  {action.action === 'glue:StartJobRun' && (
                    <>
                      <div className="trigger-confirm-action-type">Glue Job</div>
                      <div className="trigger-confirm-params">
                        <div className="trigger-confirm-param">
                          <span className="param-key">Job Name</span>
                          <span className="param-value">{action.job_name}</span>
                        </div>
                        {action.arguments && Object.entries(action.arguments).map(([k, v]) => (
                          <div key={k} className="trigger-confirm-param">
                            <span className="param-key">{k.replace(/^--/, '')}</span>
                            <span className="param-value">{String(v)}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  {action.action === 'lambda:InvokeFunction' && (
                    <>
                      <div className="trigger-confirm-action-type">Lambda Invocation</div>
                      <div className="trigger-confirm-params">
                        <div className="trigger-confirm-param">
                          <span className="param-key">Function</span>
                          <span className="param-value">{action.function_name}</span>
                        </div>
                        {action.payload && Object.entries(action.payload).map(([k, v]) => (
                          <div key={k} className="trigger-confirm-param">
                            <span className="param-key">{k}</span>
                            <span className="param-value">{String(v)}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ))}
              {pendingTrigger.hasGenericJobs && (
                <div className="trigger-generic-option">
                  <div className="generic-option-title">Run Scope</div>
                  <label className="generic-option-label">
                    <input
                      type="radio"
                      name="run-scope"
                      checked={genericAcIdOverride === ''}
                      onChange={() => setGenericAcIdOverride('')}
                    />
                    Run all audit controls for this entity + system
                  </label>
                  <label className="generic-option-label">
                    <input
                      type="radio"
                      name="run-scope"
                      checked={genericAcIdOverride !== ''}
                      onChange={() => setGenericAcIdOverride(String(auditControlId))}
                    />
                    Run for specific Audit Control ID(s) only
                  </label>
                  {genericAcIdOverride !== '' && (
                    <input
                      type="text"
                      className="generic-ac-id-input"
                      value={genericAcIdOverride}
                      onChange={(e) => setGenericAcIdOverride(e.target.value)}
                      placeholder="e.g. 7062, 7063, 7064"
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                </div>
              )}
              <p className="trigger-confirm-message">{pendingTrigger.preview.message}</p>
            </div>
            <div className="trigger-confirm-footer">
              <button className="trigger-confirm-cancel" onClick={cancelTrigger}>Cancel</button>
              <button className="trigger-confirm-execute" onClick={confirmTrigger}>Trigger</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ExecutionPipeline
